import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { SeeInAction } from "@/components/SeeInAction";

import { Problem } from "@/components/Problem";
import { Vision } from "@/components/Vision";
import { Features } from "@/components/Features";
import { FeatureShowcase } from "@/components/FeatureShowcase";
import { HowItWorks } from "@/components/HowItWorks";
import { DashboardPreview } from "@/components/DashboardPreview";
import { WhoFor } from "@/components/WhoFor";
import { FeaturesExplained } from "@/components/FeaturesExplained";
import { AiAgentSection } from "@/components/AiAgentSection";
import { AiShowcase } from "@/components/AiShowcase";

import { RoiSavings } from "@/components/RoiSavings";

import { TrustSecurity } from "@/components/TrustSecurity";
import { Comparison } from "@/components/Comparison";
import { ValueStack } from "@/components/ValueStack";
import { Pricing } from "@/components/Pricing";
import { Faq } from "@/components/Faq";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "AdKavi — AI Ad Platform for Meta, TikTok & Google" },
      {
        name: "description",
        content:
          "AI-powered ad platform to launch, optimize, and scale campaigns across Meta, TikTok, Google & YouTube — all from one dashboard.",
      },
      { property: "og:title", content: "AdKavi — Run Smarter Ads with AI" },
      {
        property: "og:description",
        content:
          "Generate ads, videos, and voiceovers with AI. Launch campaigns on Meta, TikTok, Google & YouTube from one dashboard.",
      },
      { property: "og:url", content: "https://adkavi.ai/" },
    ],
    links: [{ rel: "canonical", href: "https://adkavi.ai/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "AdKavi",
          url: "https://adkavi.ai",
          logo: "https://adkavi.ai/favicon.ico",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "AdKavi",
          url: "https://adkavi.ai",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { q: "Do I need experience running ads to use AdKavi?", a: "Not at all. AdKavi was built for people with zero ads experience. Our AI handles everything — you just describe your business and your goal and AdKavi does the rest." },
            { q: "Will AdKavi work for my industry?", a: "Yes. AdKavi works for any business — e-commerce, real estate, healthcare, restaurants, coaches, agencies, content creators and more." },
            { q: "Do I need a big ad budget?", a: "No. You can start with as little as $5 per day. AdKavi optimizes your budget automatically so every dollar works as hard as possible." },
            { q: "Is my business data and ad account safe?", a: "100%. We connect via official OAuth — we never see or store your passwords. All data is fully encrypted and never sold or shared with anyone." },
            { q: "Can I cancel anytime?", a: "Yes. No contracts, no commitments. Cancel anytime directly from your dashboard in one click." },
            { q: "What platforms does AdKavi support?", a: "Meta (Facebook & Instagram), TikTok, Google Search & Display, and YouTube — with more platforms coming soon." },
            { q: "How is AdKavi different from hiring a marketing agency?", a: "AdKavi gives you everything an agency does — strategy, creative, campaigns, optimization, and reporting — at a fraction of the cost, with full transparency and control." },
          ].map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Navbar />
      <main>
        <Hero />
        <SeeInAction />
        
        <Problem />
        <Vision />
        <Features />
        <FeatureShowcase />
        <AiShowcase />
        <AiAgentSection />
        <HowItWorks />
        <DashboardPreview />
        <WhoFor />
        <RoiSavings />
        

        <TrustSecurity />
        <FeaturesExplained />
        <Comparison />
        <ValueStack />
        <Pricing />
        <Faq />
      </main>
      <Footer />
    </div>
  );
}
